import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from '../firebase';
import { TrendingUp, Package, DollarSign, Calendar, AlertCircle, ShoppingCart, BarChart3, Database } from 'lucide-react';
import { Link } from 'react-router-dom';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalRecords: 0,
    totalRevenue: 0,
    totalDelivered: 0,
    totalEggTray: 0,
    totalPaymentReceived: 0,
    lastRecordPaymentJama: 0,
    todayRecords: 0,
    currentTrayJama: 0
  });
  const [loading, setLoading] = useState(true);
  const [recentSales, setRecentSales] = useState([]);
  const [timeFilter, setTimeFilter] = useState('thisMonth');
  const [averages, setAverages] = useState({
    avgDelivered: 0,
    avgEggTray: 0,
    avgOGRate: 0,
    avgRate: 0,
    avgTotalPrice: 0,
    avgPaymentReceived: 0,
    recordCount: 0
  });
  const [allSalesData, setAllSalesData] = useState([]);
  const [averagesLoading, setAveragesLoading] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (allSalesData.length > 0) {
      calculateAverages();
    }
  }, [timeFilter, allSalesData]);

  const getDateRange = (filter) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    switch (filter) {
      case 'thisMonth':
        const thisMonthStart = new Date(currentYear, currentMonth, 1);
        const thisMonthEnd = new Date(currentYear, currentMonth + 1, 0);
        return {
          start: thisMonthStart.toISOString().split('T')[0],
          end: thisMonthEnd.toISOString().split('T')[0]
        };
      case 'previousMonth':
        const prevMonthStart = new Date(currentYear, currentMonth - 1, 1);
        const prevMonthEnd = new Date(currentYear, currentMonth, 0);
        return {
          start: prevMonthStart.toISOString().split('T')[0],
          end: prevMonthEnd.toISOString().split('T')[0]
        };
      case 'thisYear':
        const thisYearStart = new Date(currentYear, 0, 1);
        const thisYearEnd = new Date(currentYear, 11, 31);
        return {
          start: thisYearStart.toISOString().split('T')[0],
          end: thisYearEnd.toISOString().split('T')[0]
        };
      case 'previousYear':
        const prevYearStart = new Date(currentYear - 1, 0, 1);
        const prevYearEnd = new Date(currentYear - 1, 11, 31);
        return {
          start: prevYearStart.toISOString().split('T')[0],
          end: prevYearEnd.toISOString().split('T')[0]
        };
      default:
        return { start: null, end: null };
    }
  };

  const getFilterLabel = (filter) => {
    switch (filter) {
      case 'thisMonth':
        return 'This Month';
      case 'previousMonth':
        return 'Previous Month';
      case 'thisYear':
        return 'This Year';
      case 'previousYear':
        return 'Previous Year';
      default:
        return 'This Month';
    }
  };

  const calculateAverages = () => {
    setAveragesLoading(true);
    
    // Filter data based on time filter
    const dateRange = getDateRange(timeFilter);
    let filteredData = allSalesData;
    
    if (dateRange.start && dateRange.end) {
      filteredData = allSalesData.filter(sale => 
        sale.date >= dateRange.start && sale.date <= dateRange.end
      );
    }

    // Calculate averages for filtered data
    const avgDelivered = filteredData.length > 0 ? 
      filteredData.reduce((sum, sale) => sum + (sale.delivered || 0), 0) / filteredData.length : 0;
    const avgEggTray = filteredData.length > 0 ? 
      filteredData.reduce((sum, sale) => sum + (sale.eggTray || 0), 0) / filteredData.length : 0;
    const avgOGRate = filteredData.length > 0 ? 
      filteredData.reduce((sum, sale) => sum + (sale.ogRate || 0), 0) / filteredData.length : 0;
    const avgRate = filteredData.length > 0 ? 
      filteredData.reduce((sum, sale) => sum + (sale.rate || 0), 0) / filteredData.length : 0;
    const avgTotalPrice = filteredData.length > 0 ? 
      filteredData.reduce((sum, sale) => sum + (sale.totalPrice || 0), 0) / filteredData.length : 0;
    const avgPaymentReceived = filteredData.length > 0 ? 
      filteredData.reduce((sum, sale) => sum + (sale.paymentReceived || 0), 0) / filteredData.length : 0;

    setAverages({
      avgDelivered,
      avgEggTray,
      avgOGRate,
      avgRate,
      avgTotalPrice,
      avgPaymentReceived,
      recordCount: filteredData.length
    });
    
    setAveragesLoading(false);
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Get all sales
      const salesQuery = query(collection(db, 'sales'), orderBy('date', 'desc'));
      const salesSnapshot = await getDocs(salesQuery);
      const salesData = salesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Store all sales data for averages calculation
      setAllSalesData(salesData);

      // Calculate stats (unchanged)
      const totalRevenue = salesData.reduce((sum, sale) => sum + (sale.totalPrice || 0), 0);
      const totalDelivered = salesData.reduce((sum, sale) => sum + (sale.delivered || 0), 0);
      const totalEggTray = salesData.reduce((sum, sale) => sum + (sale.eggTray || 0), 0);
      const totalPaymentReceived = salesData.reduce((sum, sale) => sum + (sale.paymentReceived || 0), 0);
      
      // Get last record's Payment Jama
      const lastRecordPaymentJama = salesData.length > 0 ? salesData[0].paymentJama || 0 : 0;
      
      // Today's records
      const today = new Date().toISOString().split('T')[0];
      const todayRecords = salesData.filter(sale => sale.date === today).length;

      // Current tray jama (from last record)
      const currentTrayJama = salesData.length > 0 ? salesData[0].trayJama || 0 : 0;

      setStats({
        totalRecords: salesData.length,
        totalRevenue,
        totalDelivered,
        totalEggTray,
        totalPaymentReceived,
        lastRecordPaymentJama,
        todayRecords,
        currentTrayJama
      });

      // Recent sales (last 5)
      setRecentSales(salesData.slice(0, 5));

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatNumber = (number) => {
    return parseFloat(number || 0).toFixed(2);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <DollarSign className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Total Revenue</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{formatCurrency(stats.totalRevenue)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Package className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Total Egg Tray</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{formatNumber(stats.totalEggTray)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Total Delivered</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{formatNumber(stats.totalDelivered)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-orange-600" />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Total Records</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.totalRecords}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-600" />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Today's Records</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.todayRecords}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertCircle className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Current Tray Jama</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{formatNumber(stats.currentTrayJama)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <DollarSign className="h-5 w-5 sm:h-6 sm:w-6 text-indigo-600" />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Payment Received</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{formatCurrency(stats.totalPaymentReceived)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 bg-pink-100 rounded-lg">
              <DollarSign className="h-5 w-5 sm:h-6 sm:w-6 text-pink-600" />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Last Record Payment Jama</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{formatCurrency(stats.lastRecordPaymentJama)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Averages Section */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <div className="flex items-center">
              <BarChart3 className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 mr-2 sm:mr-3" />
              <h3 className="text-base sm:text-lg font-medium text-gray-900">Average Statistics</h3>
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-xs sm:text-sm font-medium text-gray-700">Time Period:</label>
              <select
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.target.value)}
                className="px-2 sm:px-3 py-1 border border-gray-300 rounded-md text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={averagesLoading}
              >
                <option value="thisMonth">This Month</option>
                <option value="previousMonth">Previous Month</option>
                <option value="thisYear">This Year</option>
                <option value="previousYear">Previous Year</option>
              </select>
              {averagesLoading && (
                <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-blue-600"></div>
              )}
            </div>
          </div>
          <p className="text-xs sm:text-sm text-gray-600 mt-2">
            Showing averages for {getFilterLabel(timeFilter)} ({averages.recordCount} records)
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 p-4 sm:p-6">
          <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-600">Average Delivered</p>
                <p className="text-lg sm:text-xl font-bold text-gray-900">{formatNumber(averages.avgDelivered)}</p>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg">
                <ShoppingCart className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-600">Average Egg Tray</p>
                <p className="text-lg sm:text-xl font-bold text-gray-900">{formatNumber(averages.avgEggTray)}</p>
              </div>
              <div className="p-2 bg-green-100 rounded-lg">
                <Package className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-600">Average OG Rate</p>
                <p className="text-lg sm:text-xl font-bold text-gray-900">{formatCurrency(averages.avgOGRate)}</p>
              </div>
              <div className="p-2 bg-purple-100 rounded-lg">
                <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-600">Average Rate</p>
                <p className="text-lg sm:text-xl font-bold text-gray-900">{formatCurrency(averages.avgRate)}</p>
              </div>
              <div className="p-2 bg-orange-100 rounded-lg">
                <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600" />
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-600">Average Total Price</p>
                <p className="text-lg sm:text-xl font-bold text-gray-900">{formatCurrency(averages.avgTotalPrice)}</p>
              </div>
              <div className="p-2 bg-yellow-100 rounded-lg">
                <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-600">Average Payment Received</p>
                <p className="text-lg sm:text-xl font-bold text-gray-900">{formatCurrency(averages.avgPaymentReceived)}</p>
              </div>
              <div className="p-2 bg-indigo-100 rounded-lg">
                <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-600" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <h3 className="text-base sm:text-lg font-medium text-gray-900">Quick Actions</h3>
        </div>
        <div className="p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Link
              to="/farm-records"
              className="flex items-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <div className="p-2 bg-blue-100 rounded-lg">
                <Database className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <h4 className="text-sm font-medium text-blue-900">Farm Records</h4>
                <p className="text-xs text-blue-700">Manage production and inventory</p>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Recent Sales */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <h3 className="text-base sm:text-lg font-medium text-gray-900">Recent Sales Records</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Delivered
                </th>
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Egg Tray
                </th>
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Price
                </th>
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment Received
                </th>
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Farmer Name
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentSales.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 sm:py-12 text-center text-gray-500 text-sm">
                    No recent sales found
                  </td>
                </tr>
              ) : (
                recentSales.map((sale) => (
                  <tr key={sale.id} className="hover:bg-gray-50">
                    <td className="px-2 sm:px-4 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                      {formatDate(sale.date)}
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                      {formatNumber(sale.delivered)}
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                      {formatNumber(sale.eggTray)}
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-medium text-gray-900">
                      {formatCurrency(sale.totalPrice)}
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                      {formatCurrency(sale.paymentReceived)}
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                      {sale.farmerName || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 